import { useState } from 'react'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/hooks/useConfirm'
import { apiService } from '@/services/api'
import { toast } from 'sonner'

interface BlogGenerationLoadingStateProps {
  /** Post ID for cancel API call */
  postId: string
  /** Current AI generation phase */
  currentPhase?: string
  /** Callback when user successfully cancels generation (closes drawer) */
  onCancel: () => void
}

/**
 * Phase descriptions mapping for user-friendly display
 */
const getPhaseInfo = (phase?: string): { icon: string; text: string } => {
  const phaseMap: Record<string, { icon: string; text: string }> = {
    researching: { icon: '🔍', text: 'Researching competitors and industry trends...' },
    generating_headlines: { icon: '✍️', text: 'Generating catchy headlines...' },
    awaiting_headline_approval: { icon: '⏸️', text: 'Waiting for your headline selection...' },
    outlining: { icon: '📝', text: 'Creating a detailed outline...' },
    awaiting_outline_approval: { icon: '⏸️', text: 'Review the outline when ready...' },
    writing: { icon: '🖊️', text: 'Writing your article...' },
    awaiting_length_decision: { icon: '⏸️', text: 'Article is longer than expected...' },
    optimizing: { icon: '✨', text: 'Optimizing for SEO and readability...' },
    ready: { icon: '✅', text: 'Your article is ready!' },
    failed: { icon: '❌', text: 'Generation failed' }
  }

  // Return mapped phase or fallback
  return phaseMap[phase || ''] || { icon: '🔍', text: 'Researching top headlines...' }
}

/**
 * Loading state component for blog post AI generation
 *
 * Displays animated spinner, current generation phase, elapsed time, and cancel button.
 * Handles cancel API call with error handling (403 Forbidden, 404 Not Found, network errors).
 * Respects dark mode, prefers-reduced-motion, and provides full accessibility support.
 *
 * @example
 * <BlogGenerationLoadingState
 *   postId="abc-123-def"
 *   currentPhase="writing"
 *   onCancel={() => closeDrawer()}
 * />
 */
export function BlogGenerationLoadingState({
  postId,
  currentPhase,
  onCancel
}: BlogGenerationLoadingStateProps) {
  const { confirm, ConfirmDialog } = useConfirm()
  const [isCanceling, setIsCanceling] = useState(false)
  const phaseInfo = getPhaseInfo(currentPhase)

  const handleCancel = async () => {
    confirm({
      title: 'Cancel Generation?',
      message: 'Are you sure you want to cancel this article generation? This action cannot be undone.',
      confirmLabel: 'Yes, Cancel',
      cancelLabel: 'Continue Writing',
      variant: 'destructive',
      onConfirm: async () => {
        setIsCanceling(true)
        try {
          // Call the cancel API endpoint
          await apiService.blog.aiWriter.cancel(postId)
          toast.success('Generation canceled successfully')
          onCancel() // Close the drawer
        } catch (error: any) {
          // Handle authorization errors
          if (error.response?.status === 403) {
            toast.error('You do not have permission to cancel this generation')
          } else if (error.response?.status === 404) {
            toast.error('Post not found or already completed')
          } else {
            toast.error('Failed to cancel generation. Please try again.')
          }
          console.error('Cancel generation error:', error)
        } finally {
          setIsCanceling(false)
        }
      }
    })
  }

  return (
    <>
      <div
        className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-card text-card-foreground"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {/* Phase Information */}
        <div className="text-center space-y-4 mb-6 w-full max-w-md">
          <div className="text-4xl mb-2" aria-hidden="true">
            {phaseInfo.icon}
          </div>
          <p className="text-lg font-medium text-foreground">
            {phaseInfo.text}
          </p>

          {/* Animated Progress Bar */}
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full animate-progress"
              style={{
                animation: 'progress 2s ease-in-out infinite'
              }}
            />
          </div>
        </div>

        {/* Progress bar animation keyframes */}
        <style>{`
          @keyframes progress {
            0% { width: 0%; margin-left: 0%; }
            50% { width: 60%; margin-left: 0%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>

        {/* Cancel Button */}
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isCanceling}
          className="mt-4 border-border hover:bg-accent hover:text-accent-foreground"
          aria-label="Cancel generation"
        >
          {isCanceling ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
          ) : (
            <X className="h-4 w-4 mr-2" aria-hidden="true" />
          )}
          {isCanceling ? 'Canceling...' : 'Cancel Generation'}
        </Button>

        {/* Screen reader announcement */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {phaseInfo.text}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </>
  )
}
